import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Trash2, Calendar } from 'lucide-react';
import { format, isAfter, differenceInDays } from 'date-fns';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { BRAND_COLORS, SYSTEM_COLORS, NEUTRAL_COLORS } from '../../../constants/brandPalette';

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  planning:    { label: 'Planning',    bg: '#f3f0ff', text: '#6d28d9', dot: '#7c3aed' },
  active:      { label: 'Active',      bg: '#eff6ff', text: '#1d4ed8', dot: '#3b82f6' },
  in_progress: { label: 'In Progress', bg: '#eff6ff', text: '#1d4ed8', dot: '#3b82f6' },
  inProgress:  { label: 'In Progress', bg: '#eff6ff', text: '#1d4ed8', dot: '#3b82f6' },
  on_hold:     { label: 'On Hold',     bg: '#fffbeb', text: '#b45309', dot: '#f59e0b' },
  onHold:      { label: 'On Hold',     bg: '#fffbeb', text: '#b45309', dot: '#f59e0b' },
  completed:   { label: 'Completed',   bg: '#f0fdf4', text: '#15803d', dot: '#22c55e' },
  cancelled:   { label: 'Cancelled',   bg: '#fff1f2', text: '#be123c', dot: '#f43f5e' },
  archived:    { label: 'Archived',    bg: '#f8fafc', text: '#475569', dot: '#94a3b8' },
};

const getStatusCfg = (status) =>
  STATUS_CONFIG[status] || {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
    bg: '#f8fafc', text: '#475569', dot: '#94a3b8',
  };

// ── Helpers ───────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = [
  { bg: '#dbeafe', text: '#1d4ed8' },
  { bg: '#dcfce7', text: '#15803d' },
  { bg: '#fce7f3', text: '#9d174d' },
  { bg: '#fef3c7', text: '#92400e' },
  { bg: '#ede9fe', text: '#5b21b6' },
  { bg: '#ffedd5', text: '#9a3412' },
  { bg: '#cffafe', text: '#0e7490' },
];

const getInitials = (name = '') =>
  name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

const avatarColor = (name = '') => AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length] || AVATAR_PALETTE[0];

const parseDate = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
};

const getUserId = (u) => u?.id || u?.uid || u?.user_id || u?.sub || null;

// ── Avatar ────────────────────────────────────────────────────────────────────

const Avatar = ({ name = '', photoUrl = null, size = 28 }) => {
  const { bg, text } = avatarColor(name);
  return (
    <div
      title={name}
      style={{
        width: size, height: size,
        borderRadius: '50%',
        backgroundColor: bg,
        color: text,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.36,
        fontWeight: 700,
        flexShrink: 0,
        border: '2px solid #fff',
        overflow: 'hidden',
      }}
    >
      {photoUrl
        ? <img src={photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : getInitials(name)
      }
    </div>
  );
};

// ── ProjectCard ───────────────────────────────────────────────────────────────

const ProjectCard = ({ project, onClick, canDelete = false, onDelete }) => {
  const { user } = useApiAuth();
  const currentUserId = getUserId(user);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  // ── derived ─────────────────────────────────────────────────────────────────

  const members = (Array.isArray(project?.members) && project.members.length > 0)
    ? project.members
    : project?.team || [];

  const progress = Math.max(0, Math.min(100, Number(project?.progress) || 0));

  const totalTasks = project?.total_tasks ?? project?.tasks_count
    ?? (Array.isArray(project?.tasks) ? project.tasks.length : null);
  const completedTasks = project?.completed_tasks
    ?? (Array.isArray(project?.tasks)
      ? project.tasks.filter(t => t.status === 'completed' || t.completed).length
      : null);

  const projectManagerId =
    project?.project_manager_id || project?.project_manager ||
    project?.projectManager || project?.user_id ||
    project?.created_by || project?.createdBy;

  const manager = projectManagerId
    ? members.find(m => String(m?.user_id || m?.id) === String(projectManagerId))
    : members[0];

  const managerName = manager?.name || null;
  const managerPhoto = manager?.photoUrl || manager?.photo_url || manager?.profileImage || null;

  const endDate = parseDate(project?.endDate || project?.end_date);

  const dueDateStyle = () => {
    if (!endDate) return { color: SYSTEM_COLORS.textMuted };
    const daysLeft = differenceInDays(endDate, new Date());
    if (isAfter(new Date(), endDate)) return { color: '#dc2626', fontWeight: 600 };
    if (daysLeft <= 7) return { color: '#ea580c', fontWeight: 600 };
    return { color: SYSTEM_COLORS.textMuted };
  };

  const progressColor = progress >= 75 ? '#22c55e' : progress >= 40 ? BRAND_COLORS.primary : '#f59e0b';

  const statusCfg = getStatusCfg(project?.status);

  // Footer avatars: show up to 3; if >3, third slot becomes "+N"
  const avatarSlots = members.length <= 3
    ? members
    : members.slice(0, 2);
  const overflowCount = members.length > 3 ? members.length - 2 : 0;

  return (
    <div
      className="group relative flex flex-col rounded-2xl border cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
      style={{
        backgroundColor: SYSTEM_COLORS.shellSurface,
        borderColor: SYSTEM_COLORS.borderSoft,
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      }}
      onClick={onClick}
    >
      {/* Body */}
      <div className="flex flex-col flex-1 gap-2 p-5">

        {/* Title + three-dot menu */}
        <div className="flex items-start gap-2">
          <h3
            className="flex-1 text-base font-bold leading-snug line-clamp-2"
            style={{ color: NEUTRAL_COLORS.black }}
          >
            {project?.name || 'Untitled Project'}
          </h3>

          <div className="relative flex-shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
              className="flex items-center justify-center transition-colors rounded-lg"
              style={{
                width: 28, height: 28,
                color: SYSTEM_COLORS.textMuted,
                backgroundColor: menuOpen ? SYSTEM_COLORS.borderSoft : 'transparent',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = SYSTEM_COLORS.borderSoft; }}
              onMouseLeave={e => { if (!menuOpen) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <MoreVertical size={15} />
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 z-50 py-1 mt-1 border shadow-xl w-36 rounded-xl"
                style={{
                  backgroundColor: SYSTEM_COLORS.shellSurface,
                  borderColor: SYSTEM_COLORS.borderSoft,
                }}
                onClick={e => e.stopPropagation()}
              >
                {canDelete && typeof onDelete === 'function' ? (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2.5 px-3.5 py-2 text-sm font-medium rounded-lg transition-colors"
                    style={{ color: '#dc2626' }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#fff1f2'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      onDelete(project);
                    }}
                  >
                    <Trash2 size={13} />
                    Delete
                  </button>
                ) : (
                  <p
                    className="px-3.5 py-2 text-xs"
                    style={{ color: SYSTEM_COLORS.textMuted }}
                  >
                    No actions
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {project?.description && (
          <p className="text-sm line-clamp-2 " style={{ color: SYSTEM_COLORS.textMuted }}>
            {project.description}
          </p>
        )}

        {/* Status badge */}
        <div>
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
            style={{ backgroundColor: statusCfg.bg, color: statusCfg.text }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: statusCfg.dot }} />
            {statusCfg.label}
          </span>
        </div>

        {/* Progress */}
        <div className="space-y-1.5 bg-slate-100 rounded-lg px-3.5 py-2.5" >
          {/* Row 1: label + percentage */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium" style={{ color: SYSTEM_COLORS.textMuted }}>
              Progress
            </span>
            <span className="text-sm font-bold tabular-nums" style={{ color: NEUTRAL_COLORS.black }}>
              {progress}%
            </span>
          </div>

          {/* Row 2: bar */}
          <div
            className="w-full h-3 overflow-hidden rounded-full"
            style={{ backgroundColor: SYSTEM_COLORS.borderSoft }}
          >
            <div
              className="h-full transition-all duration-500 rounded-full"
              style={{ width: `${progress}%`, backgroundColor: progressColor }}
            />
          </div>

          {/* Row 3: task count */}
          {totalTasks != null && (
            <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
              <span className="font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
                {completedTasks ?? 0}/{totalTasks}
              </span>{' '}
              tasks completed
            </p>
          )}
        </div>

        {/* Manager */}
        {managerName && (
          <div className="flex items-center gap-2.5">
            <Avatar name={managerName} photoUrl={managerPhoto} size={30} />
            <div className="min-w-0">
              <p className="text-xs font-semibold leading-tight truncate" style={{ color: NEUTRAL_COLORS.black }}>
                {managerName}
              </p>
              <p className="text-xs leading-tight" style={{ color: SYSTEM_COLORS.textMuted }}>
                Project Manager
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-between px-5 py-3 rounded-b-2xl"
        style={{ borderTop: `2px solid ${SYSTEM_COLORS.borderSoft}` }}
      >
        {/* Stacked member avatars */}
        {members.length === 0 ? (
          <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>No members</span>
        ) : (
          <div className="flex -space-x-1.5">
            {avatarSlots.map((m, i) => (
              <Avatar key={i} name={m?.name || m?.email || '?'} photoUrl={m?.photoUrl || m?.photo_url || m?.profileImage || null} size={30} />
            ))}
            {overflowCount > 0 && (
              <div
                title={`${overflowCount} more members`}
                style={{
                  width: 30, height: 30,
                  borderRadius: '50%',
                  backgroundColor: SYSTEM_COLORS.borderSoft,
                  color: SYSTEM_COLORS.textMuted,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700,
                  border: '2px solid #fff',
                  flexShrink: 0,
                }}
              >
                +{overflowCount}
              </div>
            )}
          </div>
        )}

        {/* Due date */}
        <div className="flex items-center gap-1.5 text-xs" style={dueDateStyle()}>
          <Calendar size={12} />
          <span>{endDate ? format(endDate, 'MMM d, yyyy') : '—'}</span>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
